import React, { ComponentProps, FC, type JSX } from 'react';
import { useParams } from 'next/navigation';
import i18nConfig from 'i18n.config';
import CustomDropDown from './custom-dropdown';
import DefaultDropdown from './default-dropdown';
import Select from './option-dropdown';

export interface Option {
  name: string;
  value: string | number;
}

export interface DropdownProps extends Omit<ComponentProps<'select'>, 'key'> {
  error?: boolean;
  className?: string;
  containerClassName?: string;
  labelClassName?: string;
  items?: Array<{ label: string; value: string }>;
  label?: string;
  value?: string;
  defaultValue?: string;
  customButtonElement?: JSX.Element;
  customButtonClassNames?: (open?: boolean) => string | string;
  customMenuClassNames?: (open?: boolean) => string;
  customMenuWrapperClassNames?: string;
  selectDropdownClassNames?: string;
  selectDefaultValue?: Option;
  selectOptions?: Option[];
  selectOnChange?: (option: Option) => void;
  tabIndex?: number | undefined;
}

const Dropdown: FC<DropdownProps> = ({
  error,
  className = '',
  containerClassName = '',
  labelClassName,
  label,
  items,
  onChange,
  value,
  customButtonElement,
  customButtonClassNames,
  customMenuClassNames,
  customMenuWrapperClassNames,
  selectDropdownClassNames,
  selectDefaultValue,
  selectOptions,
  selectOnChange,
  children,
  tabIndex = undefined,
  ...props
}) => {
  const { locale } = useParams();
  const langCode = locale.split('-');
  const direction = i18nConfig.rtlLngs.indexOf(langCode[0]) > -1 ? 'rtl' : 'ltr';
  return (
    <>
      {customButtonElement ? (
        <CustomDropDown
          buttonElement={customButtonElement}
          buttonClassNames={customButtonClassNames}
          menuClassNames={customMenuClassNames}
          menuWrapperClassNames={customMenuWrapperClassNames}
        >
          {children}
        </CustomDropDown>
      ) : selectOptions ? (
        <Select
          error={error}
          label={label}
          labelClassName={labelClassName}
          selectButtonClassName={selectDropdownClassNames}
          defaultValue={selectDefaultValue}
          options={selectOptions}
          onChange={selectOnChange}
        />
      ) : (
        <DefaultDropdown
          className={className}
          containerClassName={containerClassName}
          labelClassName={labelClassName}
          label={label}
          items={items ?? []}
          onChange={onChange}
          value={value}
          direction={direction}
          tabIndex={tabIndex}
          {...props}
        />
      )}
    </>
  );
};

export default Dropdown;
